import React, { createContext, useState, useContext, useEffect } from "react";
import { authApi } from "../api/authApi"; // Change this line
import { analyticsApi } from "../api/analyticsApi";
import { getAnalyticsVisitorId } from "../utils/analyticsSession";

const AuthContext = createContext();
const SUPPORT_ADMIN_TOKEN_KEY = "support-session-admin-token";
const SUPPORT_ADMIN_USER_KEY = "support-session-admin-user";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============================================================
  // Initialize auth state from localStorage
  // ============================================================

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");

        if (token && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);

            // Verify token is still valid
            const response = await authApi.getCurrentUser();
            const currentUser = response.data?.data || response.data;
            if (currentUser) {
              setUser(currentUser);
              localStorage.setItem("user", JSON.stringify(currentUser));
            }
          } catch (error) {
            // If token is invalid, clear storage
            console.error("Invalid token:", error);
            authApi.logout();
            setUser(null);
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // ============================================================
  // Register
  // ============================================================

  const register = async (userData) => {
    try {
      setError(null);

      const response = await authApi.register(userData);
      return response;
    } catch (error) {
      console.error("Registration error:", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Registration failed. Please try again.";

      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // ============================================================
  // Login
  // ============================================================

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authApi.login(email, password);

      const loggedInUser = response.data?.user || response.data?.data?.user;

      if (loggedInUser) {
        setUser(loggedInUser);
        if (!loggedInUser.MustChangePassword) {
          try {
            await analyticsApi.trackSessionEvent({
              visitorId: getAnalyticsVisitorId(),
              eventType: "login",
            });
          } catch (analyticsError) {
            console.error("Could not record login analytics:", analyticsError);
          }
        }
      }

      return response;
    } catch (error) {
      console.error("Login error:", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Login failed. Please try again.";

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Logout
  // ============================================================

  const logout = () => {
    sessionStorage.removeItem(SUPPORT_ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(SUPPORT_ADMIN_USER_KEY);
    if (user) {
      try {
        const token = localStorage.getItem("token");
        analyticsApi
          .trackSessionEvent({
            visitorId: getAnalyticsVisitorId(),
            eventType: "logout",
          }, token)
          .catch((analyticsError) => {
            console.error("Could not record logout analytics:", analyticsError);
          });
      } catch (analyticsError) {
        console.error("Could not prepare logout analytics:", analyticsError);
      }
    }
    authApi.logout();
    setUser(null);
    setError(null);
  };

  // ============================================================
  // Get current user
  // ============================================================

  const getCurrentUser = async () => {
    try {
      const response = await authApi.getCurrentUser();
      const currentUser = response.data?.data || response.data;

      if (currentUser) {
        setUser(currentUser);
        localStorage.setItem("user", JSON.stringify(currentUser));
      }
      return currentUser;
    } catch (error) {
      console.error("Get current user error:", error);
      throw error;
    }
  };

  // ============================================================
  // Change password
  // ============================================================

  const changePassword = async (oldPassword, newPassword) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authApi.changePassword(oldPassword, newPassword);
      updateUser({ MustChangePassword: false });

      return response;
    } catch (error) {
      console.error("Change password error:", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to change password. Please try again.";

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Forgot password
  // ============================================================

  const forgotPassword = async (email) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authApi.forgotPassword(email);

      return response;
    } catch (error) {
      console.error("Forgot password error:", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to send reset instructions. Please try again.";

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authApi.verifyEmail(token);
      const verifiedUser = response.data?.data?.user;
      if (verifiedUser) {
        setUser(verifiedUser);
        try {
          await analyticsApi.trackSessionEvent({
            visitorId: getAnalyticsVisitorId(),
            eventType: "login",
          });
        } catch (analyticsError) {
          console.error(
            "Could not record email verification session analytics:",
            analyticsError,
          );
        }
      }
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Email verification failed. Please try again.";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      return await authApi.resendVerificationEmail(email);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Could not resend the verification email. Please try again.";
      throw new Error(errorMessage);
    }
  };

  const beginSupportSession = ({ token, user: supportUser }) => {
    const currentToken = localStorage.getItem("token");
    const currentUser = localStorage.getItem("user");
    const parsedCurrentUser = currentUser ? JSON.parse(currentUser) : null;
    if (
      !token ||
      !supportUser ||
      !currentToken ||
      !currentUser ||
      supportUser.SupportSession == null ||
      parsedCurrentUser?.Role !== "admin" ||
      parsedCurrentUser?.SupportSession ||
      sessionStorage.getItem(SUPPORT_ADMIN_TOKEN_KEY)
    ) {
      throw new Error("Could not start the client support session.");
    }

    sessionStorage.setItem(SUPPORT_ADMIN_TOKEN_KEY, currentToken);
    sessionStorage.setItem(SUPPORT_ADMIN_USER_KEY, currentUser);
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(supportUser));
    setUser(supportUser);
  };

  const endSupportSession = async () => {
    let endError;
    try {
      await authApi.endSupportSession();
    } catch (error) {
      endError = error;
    }

    const adminToken = sessionStorage.getItem(SUPPORT_ADMIN_TOKEN_KEY);
    const adminUser = sessionStorage.getItem(SUPPORT_ADMIN_USER_KEY);
    sessionStorage.removeItem(SUPPORT_ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(SUPPORT_ADMIN_USER_KEY);

    if (adminToken && adminUser) {
      localStorage.setItem("token", adminToken);
      localStorage.setItem("user", adminUser);
      setUser(JSON.parse(adminUser));
    } else {
      authApi.logout();
      setUser(null);
    }

    if (endError) throw endError;
  };

  // ============================================================
  // Clear error
  // ============================================================

  const clearError = () => {
    setError(null);
  };

  // ============================================================
  // Update user
  // ============================================================

  const updateUser = (updatedUser) => {
    setUser((prev) => ({
      ...prev,
      ...updatedUser,
    }));
    localStorage.setItem("user", JSON.stringify({ ...user, ...updatedUser }));
  };

  // ============================================================
  // Context value
  // ============================================================

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    getCurrentUser,
    changePassword,
    forgotPassword,
    verifyEmail,
    resendVerificationEmail,
    beginSupportSession,
    endSupportSession,
    clearError,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
